import Modal from './Modal'

/**
 * A second tap before something is called off (D-494).
 *
 * `asked`: *"could we add a confirmation modal so it isn't accidentally tapped
 * or clicked and cancelling a class a parent or teacher didn't mean to."*
 *
 * **Three controls needed it and none had one.** A parent's makeup, a parent's
 * private lesson and a teacher's booking all cancelled on a single tap, and all
 * three send a real message to the other side — so a mistap is not just a lost
 * lesson, it is a family or a teacher told their session is off.
 *
 * **One composition, three callers.** Two copies of a confirmation drift, and
 * the second copy never receives the first's fixes — which this codebase has
 * recorded on a panel, a chip and a roster row already.
 *
 * **It names what is about to happen.** A dialog reading *"Are you sure?"*
 * makes somebody who mistapped confirm the thing they did not mean to do,
 * because there is nothing in it to recognise. The lesson, the day and who is
 * affected are what turn a reflex into a decision.
 */
export default function ConfirmAction({
  title,
  body,
  confirmLabel,
  busyLabel,
  busy = false,
  dismissLabel = 'Keep it',
  onConfirm,
  onDismiss,
}: {
  title: string
  /** What is about to be called off, in enough detail to recognise it. */
  body: string
  confirmLabel: string
  busyLabel: string
  busy?: boolean
  /**
   * The safe answer, when *Keep it* is not what backing out means (D-855).
   *
   * Four of the five callers are cancelling something that exists, so the
   * default is theirs. Declaring a sub-team not called is not a cancellation of
   * anything, and *Keep it* there names nothing the reader recognises — which is
   * the failure the body text above exists to avoid, in the other button.
   */
  dismissLabel?: string
  onConfirm: () => void
  onDismiss: () => void
}) {
  return (
    /*
      <b>The overlay is `Modal` now</b> (D-515). Escape, the backdrop, the
      role, the accessible name, the scroll lock and the focus trap were all
      written here and nowhere else — so eleven other dialogs each invented
      their own answer to the same questions, and none of them trapped focus.
      This component keeps what is actually its own: the wording, and which
      answer is the safe one.

      <b>It also gains a fix it never had.</b> The backdrop dismissed on any
      click whose target was the overlay, so a drag out of the body released
      over the dim area closed it. Harmless here, where there is nothing to
      type — and the same handler on `ReportAbsence` would have discarded a
      filled-in absence form. Fixed once, in the shell.
    */
    <Modal
      role="alertdialog"
      onDismiss={onDismiss}
      labelledBy="confirm-action-title"
      describedBy="confirm-action-body"
      panelClassName="w-full max-w-[22rem] rounded-2xl border border-line bg-surface p-5 shadow-2xl"
    >
      <>
        <h2 id="confirm-action-title" className="font-display text-lg">
          {title}
        </h2>

        <p id="confirm-action-body" className="mt-1.5 text-[13px] text-ink-3">
          {body}
        </p>

        {/*
          <b>Keeping it is the wider, plainer target and it comes first.</b>
          The dialog exists because somebody may have arrived here by accident,
          so the safe answer is the one under the thumb — and the destructive
          one is not the accent colour every other primary button in the app
          uses, because this is not the action we are encouraging.
        */}
        <div className="mt-4 flex flex-col gap-2">
          <button
            type="button"
            onClick={onDismiss}
            className="min-h-[44px] w-full rounded-lg border border-line bg-surface px-3 py-2 text-[13.5px] font-semibold"
          >
            {dismissLabel}
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="min-h-[44px] w-full rounded-lg border border-crit bg-crit-bg px-3 py-2 text-[13.5px] font-bold text-crit disabled:opacity-60"
          >
            {busy ? busyLabel : confirmLabel}
          </button>
        </div>
      </>
    </Modal>
  )
}
